import { ClockCounterClockwise } from '@phosphor-icons/react';
import { Card, Flex, message, Space, Table, TableColumnsType, Tabs } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { BalanceList, BalanceLogType, fetchBalanceLog } from '~/services/pay';
import { G_EmptyStr, getProductName } from '~/utils/const';
import { formatToDateTime } from '~/utils/date';
import { formatAmount } from '~/utils/function';
import { useUserInfo } from '~/hooks/useUserInfo';
import LoginMask from '~/components/LoginMask';
import { useSEO } from '~/hooks/useSEO';
import { BALANCE_SEO } from '~/seo/site';

// 充值记录独有的商品与金额列，数据来自关联的支付订单
const rechargeColumns: TableColumnsType<BalanceList> = [
  {
    title: '商品',
    dataIndex: 'productID',
    width: 150,
    render: (_, record) => getProductName(record.productID),
  },
  {
    title: '金额',
    dataIndex: 'amount',
    width: 110,
    render: (_, record) =>
      record.amount ? formatAmount(record.amount, { currency: '¥' }) : G_EmptyStr,
  },
];

const BalanceLog = ({ isTabItem = false }: { isTabItem?: boolean }) => {
  useSEO({ ...BALANCE_SEO, skip: isTabItem });

  const { userInfo } = useUserInfo();
  const [activeType, setActiveType] = useState<BalanceLogType>('consume');
  const [dataSource, setDataSource] = useState<BalanceList[]>([]);
  const [loading, setLoading] = useState(false);

  const columns: TableColumnsType<BalanceList> = [
    {
      title: '交易详情',
      dataIndex: 'description',
      width: 160,
    },
    ...(activeType === 'recharge' ? rechargeColumns : []),
    {
      title: '积分',
      dataIndex: 'points',
      render: (_, record) => {
        const points = record.points;
        // 会员、智能体等按金额结算的交易不涉及积分，流水积分为 0，展示为空
        if (points === 0) return <>{G_EmptyStr}</>;
        const isNegative = points < 0;
        const formattedPoints = formatAmount(points, { fixed: 3 });
        return (
          <>
            {isNegative ? (
              <span className="amount-decrease">{formattedPoints}</span>
            ) : (
              <span className="amount-increase">+{formattedPoints}</span>
            )}
          </>
        );
      },
    },
    {
      title: '交易时间',
      dataIndex: 'optAt',
      render: (_, record) => formatToDateTime(record.optAt),
    },
    {
      title: '订单号',
      dataIndex: 'orderNO',
      render: (_, record) => record.orderNO || G_EmptyStr,
    },
  ];

  const loadData = useCallback(async (type: BalanceLogType) => {
    setLoading(true);
    try {
      const { code, data, message: msg } = await fetchBalanceLog(type);
      if (code !== 0) {
        message.error(msg || '获取消费记录失败，请稍后再试！');
        return;
      }
      setDataSource(data.logs || []);
    } finally {
      setLoading(false);
    }
  }, []);

  const _Table = () => (
    <Table<BalanceList>
      size="small"
      loading={loading}
      columns={columns}
      scroll={{ x: true }}
      components={{
        table: (props: any) => (
          <table {...props} style={{ minWidth: activeType === 'recharge' ? '880px' : '660px' }} />
        ),
      }}
      locale={{ emptyText: activeType === 'consume' ? '暂无积分消费记录' : '暂无充值记录' }}
      dataSource={dataSource}
    />
  );

  useEffect(() => {
    userInfo?.id && loadData(activeType);
  }, [userInfo?.id, activeType, loadData]);

  const tabs = (
    <Tabs
      activeKey={activeType}
      onChange={(key) => setActiveType(key as BalanceLogType)}
      items={[
        { key: 'consume', label: '积分消费记录', children: <_Table /> },
        { key: 'recharge', label: '充值记录', children: <_Table /> },
      ]}
    />
  );

  return (
    <div className={isTabItem ? 'balance-log-page-tab-item' : 'balance-log-page zt-page'}>
      {!userInfo?.id && <LoginMask />}
      {isTabItem ? (
        <>
          <div className="mt-2 balance-log-header">最近充值和消费记录</div>
          {tabs}
        </>
      ) : (
        <Card
          className="zt-container"
          title={
            <Flex justify="space-between" align="center">
              <Space className="text-2xl font-semibold">
                <ClockCounterClockwise weight="bold" size={32} />
                消费记录
              </Space>
            </Flex>
          }
          bordered={false}
        >
          {tabs}
        </Card>
      )}
    </div>
  );
};
export default BalanceLog;
